import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowRight, History, RotateCcw } from "lucide-react";
import { api } from "../api/client";
import type {
  BulkAction,
  BulkOperation,
  BulkOperationRequest,
  BulkPreview,
} from "../api/types";
import { useAuth } from "../auth/AuthProvider";
import { PageHeader, Select } from "../components/ui";
import { ScreenManagementTabs } from "../components/ScreenManagementTabs";
import "./FleetBulkPage.css";

const actionLabels: Record<BulkAction, string> = {
  assign_playlist: "Assign a playlist",
  assign_layout: "Assign a Layout",
  clear_assignment: "Remove the assignment",
  set_enabled: "Enable or disable playback",
  send_command: "Send a command",
};

// The bulk command set is deliberately the safe, idempotent subset. A command
// that takes a screen off the air is a per-screen decision.
const bulkCommands = [
  { value: "sync_now", label: "Sync now" },
  { value: "reload_playback", label: "Reload playback" },
  { value: "clear_media_cache", label: "Clear media cache" },
  { value: "restart_player_process", label: "Restart the Player" },
];

export function FleetBulkPage() {
  const auth = useAuth();
  const client = useQueryClient();
  const csrf = auth.status?.csrfToken ?? "";

  const screens = useQuery({ queryKey: ["screens"], queryFn: api.screens });
  const playlists = useQuery({
    queryKey: ["playlists"],
    queryFn: () => api.playlists(),
  });
  const layouts = useQuery({
    queryKey: ["layouts"],
    queryFn: () => api.layouts(),
  });
  const operations = useQuery({
    queryKey: ["bulk-operations"],
    queryFn: () => api.bulkOperations(5),
  });

  const [selected, setSelected] = useState<string[]>([]);
  const [action, setAction] = useState<BulkAction>("assign_playlist");
  const [playlistId, setPlaylistId] = useState("");
  const [layoutId, setLayoutId] = useState("");
  const [enabled, setEnabled] = useState(true);
  const [commandType, setCommandType] = useState("sync_now");
  const [preview, setPreview] = useState<BulkPreview>();
  const [result, setResult] = useState<BulkOperation>();
  // Kept separately because apply clears the preview before the result renders.
  const [undoWindowMinutes, setUndoWindowMinutes] = useState<number>();

  const items = screens.data?.items ?? [];

  function buildRequest(): BulkOperationRequest {
    const request: BulkOperationRequest = { screenIds: selected, action };
    if (action === "assign_playlist") request.playlistId = playlistId;
    if (action === "assign_layout") request.layoutId = layoutId;
    if (action === "set_enabled") request.enabled = enabled;
    if (action === "send_command") request.commandType = commandType;
    return request;
  }

  const build = useMutation({
    mutationFn: () => api.previewBulkOperation(buildRequest()),
    onSuccess: (data) => {
      setPreview(data);
      setResult(undefined);
      setUndoWindowMinutes(data.undoWindowMinutes);
    },
  });
  const apply = useMutation({
    mutationFn: () =>
      api.applyBulkOperation(
        { ...buildRequest(), expectedChangeCount: preview?.changeCount ?? 0 },
        csrf,
      ),
    onSuccess: (data) => {
      setResult(data);
      setPreview(undefined);
      void client.invalidateQueries({ queryKey: ["screens"] });
      void client.invalidateQueries({ queryKey: ["bulk-operations"] });
    },
  });
  const undo = useMutation({
    mutationFn: (id: string) => api.undoBulkOperation(id, csrf),
    onSuccess: () => {
      setResult(undefined);
      void client.invalidateQueries({ queryKey: ["screens"] });
      void client.invalidateQueries({ queryKey: ["bulk-operations"] });
    },
  });

  const ready =
    selected.length > 0 &&
    (action !== "assign_playlist" || playlistId !== "") &&
    (action !== "assign_layout" || layoutId !== "");

  const allSelected = items.length > 0 && selected.length === items.length;

  return (
    <div className="bulk-page">
      <PageHeader
        title="Bulk changes"
        description="Apply one change to many screens. Tilecast shows exactly what will change before anything happens, including screens pulled in by a Display Group."
      />
      <ScreenManagementTabs current="bulk" />

      <div className="bulk-workspace">
        <section className="bulk-panel" aria-labelledby="bulk-screens-heading">
          <header className="bulk-panel__header">
            <div className="bulk-panel__heading">
              <h2 id="bulk-screens-heading">Screens</h2>
              <p>
                A screen in a Display Group shares that group&apos;s assignment,
                so selecting one member includes the rest. The preview lists
                them.
              </p>
            </div>
            <div className="bulk-panel__actions">
              <span className="bulk-count">
                {selected.length} of {items.length} selected
              </span>
              <button
                className="button button--quiet button--compact"
                type="button"
                disabled={items.length === 0}
                onClick={() => {
                  setPreview(undefined);
                  setSelected(allSelected ? [] : items.map((item) => item.id));
                }}
              >
                {allSelected ? "Select none" : "Select all"}
              </button>
            </div>
          </header>

          {items.length === 0 ? (
            <div className="bulk-empty">
              {screens.isLoading
                ? "Loading screens…"
                : "No screens are paired yet."}
            </div>
          ) : (
            <div className="bulk-picker">
              {items.map((item) => {
                const isSelected = selected.includes(item.id);
                return (
                  <label
                    className={`checkbox-control bulk-picker__option${
                      isSelected ? " bulk-picker__option--selected" : ""
                    }`}
                    key={item.id}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={(event) => {
                        setPreview(undefined);
                        setSelected((ids) =>
                          event.target.checked
                            ? [...ids, item.id]
                            : ids.filter((id) => id !== item.id),
                        );
                      }}
                    />
                    <span className="bulk-picker__label">
                      <span>{item.name}</span>
                      <small>
                        {item.syncGroupName
                          ? `Display Group: ${item.syncGroupName}`
                          : item.location || "No location"}
                      </small>
                    </span>
                  </label>
                );
              })}
            </div>
          )}
        </section>

        <section
          className="bulk-panel bulk-change"
          aria-labelledby="bulk-change-heading"
        >
          <header className="bulk-panel__header">
            <div className="bulk-panel__heading">
              <h2 id="bulk-change-heading">Change</h2>
            </div>
          </header>

          <div className="bulk-panel__body">
            <div className="bulk-field">
              <label htmlFor="bulk-action">Action</label>
              <Select
                id="bulk-action"
                value={action}
                onChange={(event) => {
                  setAction(event.target.value as BulkAction);
                  setPreview(undefined);
                }}
              >
                {(Object.keys(actionLabels) as BulkAction[]).map((value) => (
                  <option key={value} value={value}>
                    {actionLabels[value]}
                  </option>
                ))}
              </Select>
            </div>

            {action === "assign_playlist" && (
              <div className="bulk-field">
                <label htmlFor="bulk-playlist">Playlist</label>
                <Select
                  id="bulk-playlist"
                  value={playlistId}
                  onChange={(event) => {
                    setPlaylistId(event.target.value);
                    setPreview(undefined);
                  }}
                >
                  <option value="">Select a playlist</option>
                  {playlists.data?.items?.map((playlist) => (
                    <option key={playlist.id} value={playlist.id}>
                      {playlist.name}
                    </option>
                  ))}
                </Select>
              </div>
            )}

            {action === "assign_layout" && (
              <div className="bulk-field">
                <label htmlFor="bulk-layout">Layout</label>
                <Select
                  id="bulk-layout"
                  value={layoutId}
                  onChange={(event) => {
                    setLayoutId(event.target.value);
                    setPreview(undefined);
                  }}
                >
                  <option value="">Select a Layout</option>
                  {layouts.data?.items?.map((layout) => (
                    <option key={layout.id} value={layout.id}>
                      {layout.name}
                    </option>
                  ))}
                </Select>
                <p>Only published Layouts can be assigned.</p>
              </div>
            )}

            {action === "set_enabled" && (
              <div className="bulk-field">
                <label htmlFor="bulk-enabled">Playback</label>
                <Select
                  id="bulk-enabled"
                  value={enabled ? "enabled" : "disabled"}
                  onChange={(event) => {
                    setEnabled(event.target.value === "enabled");
                    setPreview(undefined);
                  }}
                >
                  <option value="enabled">Enable playback</option>
                  <option value="disabled">Disable playback</option>
                </Select>
              </div>
            )}

            {action === "send_command" && (
              <div className="bulk-field">
                <label htmlFor="bulk-command">Command</label>
                <Select
                  id="bulk-command"
                  value={commandType}
                  onChange={(event) => {
                    setCommandType(event.target.value);
                    setPreview(undefined);
                  }}
                >
                  {bulkCommands.map((command) => (
                    <option key={command.value} value={command.value}>
                      {command.label}
                    </option>
                  ))}
                </Select>
                <p>A command cannot be undone once a Player collects it.</p>
              </div>
            )}
          </div>

          {build.error && (
            <div className="notice notice--error" role="alert">
              {build.error.message}
            </div>
          )}

          <div className="bulk-panel__footer">
            <div className="bulk-panel__actions">
              <button
                className="button button--primary"
                type="button"
                disabled={!ready || build.isPending}
                onClick={() => build.mutate()}
              >
                {build.isPending ? "Checking…" : "Preview the change"}
              </button>
            </div>
          </div>
        </section>
      </div>

      {preview && (
        <PreviewSection
          preview={preview}
          applying={apply.isPending}
          error={apply.error?.message}
          onApply={() => apply.mutate()}
          onCancel={() => setPreview(undefined)}
        />
      )}

      {result && (
        <section className="bulk-panel" aria-labelledby="bulk-applied-heading">
          <header className="bulk-panel__header">
            <div className="bulk-panel__heading">
              <h2 id="bulk-applied-heading">Applied</h2>
            </div>
          </header>

          <div className="bulk-tally">
            <div>
              <strong>{result.appliedCount}</strong>
              <span>changed</span>
            </div>
            <div>
              <strong>{result.skippedCount}</strong>
              <span>unchanged or skipped</span>
            </div>
            {result.failedCount > 0 && (
              <div>
                <strong>{result.failedCount}</strong>
                <span>failed</span>
              </div>
            )}
          </div>

          {result.failedCount > 0 && (
            <div className="notice notice--error" role="alert">
              Some screens did not change:
              <ul>
                {result.results
                  .filter((row) => row.error)
                  .map((row) => (
                    <li key={row.screenId}>
                      {row.name}: {row.error}
                    </li>
                  ))}
              </ul>
            </div>
          )}

          {undo.error && (
            <div className="notice notice--error" role="alert">
              {undo.error.message}
            </div>
          )}

          {result.reversible && (
            <div className="bulk-panel__footer">
              <p>
                You can put this back for the next {undoWindowMinutes ?? 15}{" "}
                minutes.
              </p>
              <div className="bulk-panel__actions">
                <button
                  className="button"
                  type="button"
                  disabled={undo.isPending}
                  onClick={() => undo.mutate(result.id)}
                >
                  <RotateCcw size={15} aria-hidden="true" />{" "}
                  {undo.isPending ? "Undoing…" : "Undo this change"}
                </button>
              </div>
            </div>
          )}
        </section>
      )}

      <section className="bulk-panel" aria-labelledby="bulk-recent-heading">
        <header className="bulk-panel__header">
          <div className="bulk-panel__heading">
            <h2 id="bulk-recent-heading">Recent bulk changes</h2>
          </div>
        </header>
        {!operations.data?.length ? (
          <div className="bulk-empty">
            <History size={22} aria-hidden="true" />
            <p>No bulk changes yet.</p>
          </div>
        ) : (
          <div className="bulk-history">
            {operations.data.map((operation) => (
              <div className="bulk-history__row" key={operation.id}>
                <div className="bulk-row__copy">
                  <strong>{actionLabels[operation.action]}</strong>
                  <small>
                    {new Date(operation.createdAt).toLocaleString()} ·{" "}
                    {operation.appliedCount} screens
                    {operation.undoneAt ? " · undone" : ""}
                  </small>
                </div>
                {operation.reversible ? (
                  // Undo is consequential, so it keeps a visible outline rather
                  // than the quiet variant that reads as plain text at rest.
                  <button
                    className="button button--secondary button--compact"
                    type="button"
                    disabled={undo.isPending}
                    onClick={() => undo.mutate(operation.id)}
                  >
                    Undo
                  </button>
                ) : (
                  <span className="bulk-history__note">
                    {operation.undoneAt ? "Undone" : "Undo window closed"}
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function PreviewSection({
  preview,
  applying,
  error,
  onApply,
  onCancel,
}: {
  preview: BulkPreview;
  applying: boolean;
  error?: string;
  onApply: () => void;
  onCancel: () => void;
}) {
  return (
    <section
      className="bulk-panel bulk-panel--decision"
      aria-labelledby="bulk-preview-heading"
    >
      <header className="bulk-panel__header">
        <div className="bulk-panel__heading">
          <h2 id="bulk-preview-heading">What will change</h2>
          <p>Nothing has been applied yet. Review the rows, then confirm.</p>
        </div>
      </header>

      <div className="bulk-tally">
        <div>
          <strong>{preview.changeCount}</strong>
          <span>will change</span>
        </div>
        <div>
          <strong>{preview.unchangedCount}</strong>
          <span>already in that state</span>
        </div>
        {preview.blockedCount > 0 && (
          <div>
            <strong>{preview.blockedCount}</strong>
            <span>cannot be changed</span>
          </div>
        )}
      </div>

      {preview.warnings.map((warning) => (
        <div className="notice notice--info" key={warning} role="status">
          {warning}
        </div>
      ))}

      <div className="bulk-rows">
        {preview.screens.map((row) => (
          <div className="bulk-row" key={row.screenId}>
            <div className="bulk-row__copy">
              <strong>
                {row.name}
                {row.fromGroup ? ` (via ${row.fromGroup})` : ""}
              </strong>
              {/* The arrow is decoration; "becomes" is what a screen reader
                  needs so the two states are not read as one run-on value. */}
              <small className="bulk-transition">
                <span>{row.current}</span>
                <ArrowRight size={13} aria-hidden="true" />
                <span className="visually-hidden">becomes</span>
                <span className="bulk-transition__next">{row.next}</span>
              </small>
            </div>
            <span className="bulk-row__verdict">
              {row.blocked ? (
                <span className="status-chip status-chip--offline">
                  Skipped: {row.blocked}
                </span>
              ) : row.changes ? (
                <span className="status-chip status-chip--recent">
                  Will change
                </span>
              ) : (
                "No change"
              )}
            </span>
          </div>
        ))}
      </div>

      {error && (
        <div className="notice notice--error" role="alert">
          {error}
        </div>
      )}

      <div className="bulk-panel__footer">
        <div className="bulk-panel__actions">
          <button
            className="button button--quiet"
            type="button"
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            className="button button--primary"
            type="button"
            disabled={applying || preview.changeCount === 0}
            onClick={onApply}
          >
            {applying
              ? "Applying…"
              : `Change ${preview.changeCount} screen${preview.changeCount === 1 ? "" : "s"}`}
          </button>
        </div>
      </div>
    </section>
  );
}
